import { CENTRAL_PERMISSION_MAP, GENERAL_ROLE_DEFAULTS, type GeneralRole, type PositionRole, type RoleContext, type TeamRole } from '../../lib/roles';
import { normalizeRoleId, normalizeTeamRoleId, normalizeComplianceCategoryId } from '../../lib/idNormalization';

const TEAM_ROLE_CATEGORY_IDS: Record<string, string[]> = {
  NN: ['nurse_notes'],
  ETHIC: ['ethicSurveys'],
  med_error: ['med_error', 'med_error_incident'],
  MED_ERROR: ['med_error', 'med_error_incident'],
  RM: ['needleandsharp'],
  PPP: ['siren_bundle'],
};

export interface ScopedDepartmentRole {
  id: string;
  role: string;
  lineOfWork?: boolean;
}

export interface TeamAssignment {
  teamId: string;
  teamCode: string;
  teamName: string;
  role: string;
}

type TeamRoleInput = TeamAssignment | { id?: string; teamId?: string; teamCode?: string; role?: string } | string;

export interface UserInfo {
  id: string;
  generalRole: GeneralRole | string;
  /** Canonical role IDs (UPPERCASE). If present, used for set-union permissions. */
  roles?: string[];
  positionRole?: string;
  departmentId?: string;
  departmentIds?: string[];
  departmentRoleById?: Record<string, string>;
  departmentRoles?: { id: string; role: string }[];
  /** String codes and/or `{ role, teamId, ... }` rows from Firestore / claims. */
  teamRoles?: TeamRoleInput[];
  team_roles?: string[];
  teamCategoryIds?: string[];
  lofId?: string;
  /** Role document id (e.g. "auditor", "supervisor_nurse") */
  roleId?: string;
}

export interface ActionScope {
  departmentIds: string[];
  teamIds: string[];
}

export interface RoleScope {
  view: ActionScope;
  edit: ActionScope;
  departmentIds: string[];
  teamIds: string[];
}

export interface ResolvedRoleScope {
  roleContext: RoleContext;
  scope: RoleScope;
}

/**
 * Page context for scope resolution.
 * `pageKind: 'team'` gates access behind team_role membership.
 * `pageKind: 'normal'` uses standard department/LOF scope.
 */
export interface PageContext {
  pageKind: 'normal' | 'team';
  /** Required when pageKind is 'team'. Maps to the team_role gate (NN, ETHIC, med_error, OCC). */
  teamCategory?: string;
}

/** Normalize role to canonical UPPERCASE. Re-export for callers that need it. */
export const normalizeRole = (role: unknown): string | undefined =>
  normalizeRoleId(typeof role === 'string' ? role : undefined);

/**
 * Legacy / short department-role tokens → canonical IDs for CENTRAL_PERMISSION_MAP & LOF (normalizeRoleId is uppercase-only).
 */
const toCanonicalDepartmentRole = (role: unknown): string | undefined => {
  const n = normalizeRole(role);
  if (!n) return undefined;
  if (n === 'HWN') return 'HEAD_WARD_NURSE';
  if (n === 'SN' || n === 'SN_SUPERVISOR') return 'SUPERVISOR_NURSE';
  return n;
};

const deriveUserLineOfWorkRoleKey = (generalRole?: string, positionRole?: string): string | undefined => {
  const normalizedGeneralRole = normalizeRole(generalRole);
  const normalizedPositionRole = normalizeRole(positionRole);

  if (normalizedGeneralRole && LINE_OF_WORK_ROLE_ASSOCIATIONS[normalizedGeneralRole]) {
    return normalizedGeneralRole;
  }

  // Canonical: SUPERVISOR_NURSE
  if (normalizedGeneralRole === 'SUPERVISOR_NURSE') return 'SUPERVISOR_NURSE';
  if (normalizedGeneralRole === 'SUPERVISOR' && normalizedPositionRole === 'SUPERVISOR_NURSE') {
    return 'SUPERVISOR_NURSE';
  }
  // Legacy: SN (during migration; /roles no longer has SN doc)
  if (normalizedGeneralRole === 'SUPERVISOR' && normalizedPositionRole === 'SN') {
    return 'SUPERVISOR_NURSE';
  }

  return undefined;
};

const normalizeRoleOrVisitor = (role: string | undefined, positionRole?: string): GeneralRole | string => {
  const normalized = normalizeRole(role);

  if (normalized && CENTRAL_PERMISSION_MAP[normalized]) return normalized;

  const normalizedPosition = normalizeRole(positionRole) as PositionRole | undefined;
  const defaultRole = normalizedPosition ? GENERAL_ROLE_DEFAULTS[normalizedPosition] : undefined;

  if (defaultRole) return defaultRole;

  return 'USER';
};

const pickAllDepartmentRoles = (departmentRoles?: ScopedDepartmentRole[]) =>
  Array.from(
    new Set(
      (departmentRoles?.map((d) => toCanonicalDepartmentRole(d.role) ?? normalizeRole(d.role)) ?? []).filter(
        Boolean,
      ),
    ),
  ) as string[];

const getTeamRoleToken = (teamRole: TeamRoleInput): string | undefined => {
  if (typeof teamRole === 'string') return normalizeTeamRoleId(teamRole);
  const fallbackId = 'teamId' in teamRole ? teamRole.teamId : undefined;
  const fallback = 'id' in teamRole ? teamRole.id : fallbackId;
  return normalizeTeamRoleId(teamRole.role ?? teamRole.teamCode ?? fallback);
};

const getTeamId = (teamRole: TeamRoleInput): string | undefined => {
  if (typeof teamRole === 'string') return teamRole;
  return ('teamId' in teamRole ? teamRole.teamId : teamRole.id) ?? undefined;
};

const getTeamCode = (teamRole: TeamRoleInput): string | undefined => {
  if (typeof teamRole === 'string') return normalizeRole(teamRole);
  const fallback = 'id' in teamRole ? teamRole.id : teamRole.teamId;
  return normalizeRole(teamRole.teamCode ?? teamRole.role ?? fallback);
};

const pickPrimaryDepartmentRole = (departmentRoles?: ScopedDepartmentRole[]) =>
  toCanonicalDepartmentRole(departmentRoles?.[0]?.role) ?? normalizeRole(departmentRoles?.[0]?.role);

const pickPrimaryTeamRole = (teamRoles?: TeamRoleInput[]) =>
  teamRoles?.[0] ? getTeamRoleToken(teamRoles[0]) : undefined;

const pickPrimaryTeamId = (teamRoles?: TeamRoleInput[]) => (teamRoles?.[0] ? getTeamId(teamRoles[0]) : undefined);

const pickPrimaryTeamCode = (teamRoles?: TeamRoleInput[]) => (teamRoles?.[0] ? getTeamCode(teamRoles[0]) : undefined);

const pickAllTeamIds = (teamRoles?: TeamRoleInput[]) =>
  Array.from(new Set((teamRoles?.map((t) => getTeamId(t)) ?? []).filter(Boolean))) as string[];

/**
 * Union `team_roles` (string ids) with `teamRoles` (string or assignment objects).
 * Using only `team_roles ?? teamRoles` is unsafe: a partial `team_roles` like `["NN"]`
 * would hide `ETHIC` present only on `teamRoles` from AuthProvider / Admin merges.
 */
const mergeTeamRoleInputs = (user: UserInfo): TeamRoleInput[] => {
  const fromStrings = Array.isArray(user.team_roles)
    ? (user.team_roles.filter((r): r is string => typeof r === 'string' && r.trim().length > 0) as string[])
    : [];
  const fromObjects = Array.isArray(user.teamRoles)
    ? (user.teamRoles.filter(Boolean) as TeamRoleInput[])
    : [];
  const seen = new Set<string>();
  const out: TeamRoleInput[] = [];
  for (const entry of fromStrings) {
    const token = getTeamRoleToken(entry)?.toUpperCase();
    if (!token || seen.has(token)) continue;
    seen.add(token);
    out.push(entry);
  }
  for (const entry of fromObjects) {
    const token = getTeamRoleToken(entry)?.toUpperCase();
    if (!token || seen.has(token)) continue;
    seen.add(token);
    out.push(entry);
  }
  return out;
};

/** Team surfaces keyed off `teamAllowedCategoryIds` in nav / team pages (canonical compliance category ids). */
const GLOBAL_VIEW_TEAM_CATEGORY_IDS = [
  'nurse_notes',
  'ethicSurveys',
  'med_error_incident',
  'fall_incident',
] as const;

const GLOBAL_VIEW_ROLES = new Set(['ADMIN', 'MANAGER', 'AUDITOR']);

/**
 * Retired: LOF department lists are resolved from Firestore (for example `lofs/{lofId}/departmentRefs`)
 * in AuthProvider and merged via `mergeSupervisorLofScope`. Kept empty for backward-compatible imports.
 */
export const LINE_OF_WORK_DEPARTMENT_MAP: Record<string, string[]> = {};

export const LINE_OF_WORK_ROLE_ASSOCIATIONS: Record<string, { lineOfWorkId: string; expandScope: boolean }> = {
  // Canonical role IDs from /roles collection
  SUPERVISOR_NURSE: { lineOfWorkId: 'NURSING_SERVICES', expandScope: true },
  SUPERVISOR: { lineOfWorkId: 'NURSING_SERVICES', expandScope: true },
  HEAD_WARD_NURSE: { lineOfWorkId: 'NURSING_SERVICES', expandScope: false },
  // Legacy aliases (during migration)
  SN_SUPERVISOR: { lineOfWorkId: 'NURSING_SERVICES', expandScope: true },
  HWN: { lineOfWorkId: 'NURSING_SERVICES', expandScope: false },
};

const computeDepartmentScope = (
  generalRole: string,
  positionRole: string | undefined,
  departmentRoles?: ScopedDepartmentRole[],
  departmentId?: string,
  departmentIds?: string[],
) => {
  const normalizedRole = normalizeRole(generalRole);

  if (GLOBAL_VIEW_ROLES.has(normalizedRole)) {
    return {
      departmentIds: ['*'],
      lineOfWorkDepartmentIds: [] as string[],
    };
  }

  const scopedDepartments = new Set<string>();
  const lineOfWorkDepartmentIds = new Set<string>();

  if (departmentRoles?.length) {
    departmentRoles.forEach((deptRole) => scopedDepartments.add(deptRole.id));
  }

  if (departmentId) {
    scopedDepartments.add(departmentId);
  }

  if (departmentIds?.length) {
    departmentIds.forEach((deptId) => scopedDepartments.add(deptId));
  }

  const userLineOfWorkRoleKey = deriveUserLineOfWorkRoleKey(generalRole, positionRole);
  const userLineOfWorkConfig = userLineOfWorkRoleKey
    ? LINE_OF_WORK_ROLE_ASSOCIATIONS[userLineOfWorkRoleKey]
    : undefined;
  if (userLineOfWorkConfig?.expandScope) {
    // SN/SUPERVISOR_NURSE: line-of-work departments come from Firestore LOF resolution in AuthProvider, not a static list.
    const lofDepartmentIds = LINE_OF_WORK_DEPARTMENT_MAP[userLineOfWorkConfig.lineOfWorkId] ?? [];
    lofDepartmentIds.forEach((deptId) => lineOfWorkDepartmentIds.add(deptId));
  }

  departmentRoles?.forEach((deptRole) => {
    const normalizedDeptRole = toCanonicalDepartmentRole(deptRole.role) ?? normalizeRole(deptRole.role);

    if (!deptRole.lineOfWork || !normalizedDeptRole) return;

    const lineOfWorkConfig = LINE_OF_WORK_ROLE_ASSOCIATIONS[normalizedDeptRole];
    if (!lineOfWorkConfig) return;

    // Only expand scope if the role allows it (e.g., supervisors)
    // Roles like HWN have expandScope: false and should NOT see all departments
    if (!lineOfWorkConfig.expandScope) return;

    const lofDepartmentIds = LINE_OF_WORK_DEPARTMENT_MAP[lineOfWorkConfig.lineOfWorkId] ?? [];

    lofDepartmentIds.forEach((deptId) => lineOfWorkDepartmentIds.add(deptId));
  });

  return {
    departmentIds: Array.from(scopedDepartments),
    lineOfWorkDepartmentIds: Array.from(lineOfWorkDepartmentIds),
  };
};

const computeTeamScope = (teamRoles?: TeamRoleInput[]) =>
  Array.from(new Set((teamRoles?.map((team) => getTeamId(team)) ?? []).filter(Boolean))) as string[];

const buildRolesSet = (user: UserInfo): string[] => {
  const raw = user.roles?.length
    ? user.roles
    : [user.generalRole, (user as { role?: string }).role].filter((r): r is string => !!r && typeof r === 'string');
  const normalized = raw.map((r) => normalizeRoleId(r)).filter((r): r is string => !!r);
  return Array.from(new Set(normalized));
};

export const resolveRoleScope = (user: UserInfo, page?: PageContext): ResolvedRoleScope => {
  try {
    const rolesSet = buildRolesSet(user);
    const normalizedPositionRole = normalizeRole(user.positionRole);
    // Unknown strings (e.g. "member" → MEMBER) must not become generalRole; fall back to USER via normalizeRoleOrVisitor.
    const candidateFromRoles = rolesSet.length ? rolesSet[0] : undefined;
    const generalRole =
      candidateFromRoles && CENTRAL_PERMISSION_MAP[candidateFromRoles]
        ? candidateFromRoles
        : normalizeRoleOrVisitor(user.generalRole, normalizedPositionRole);
    const roleById =
      user.departmentRoleById ??
      (Array.isArray((user as { departmentRoles?: unknown }).departmentRoles)
        ? Object.fromEntries(
            (user as { departmentRoles?: unknown[] }).departmentRoles
              .filter((deptRole): deptRole is { id: string; role: string } => {
                return !!(
                  deptRole &&
                  typeof deptRole === 'object' &&
                  'id' in deptRole &&
                  typeof (deptRole as { id?: unknown }).id === 'string' &&
                  'role' in deptRole &&
                  typeof (deptRole as { role?: unknown }).role === 'string'
                );
              })
              .map((deptRole) => [deptRole.id, deptRole.role]),
          )
        : undefined);

    const departmentRoles: ScopedDepartmentRole[] = roleById
      ? Object.entries(roleById).map(([id, role]) => {
          const normalizedRole = toCanonicalDepartmentRole(role) ?? normalizeRole(role) ?? '';
          const lineOfWork = !!(normalizedRole && LINE_OF_WORK_ROLE_ASSOCIATIONS[normalizedRole]);
          return { id, role: normalizedRole, lineOfWork };
        })
      : [];
    const roleId = normalizeRoleId(user.roleId);
    const hasManagerRole = pickAllDepartmentRoles(departmentRoles)
      .map((r) => normalizeRoleId(r))
      .includes('MANAGER');
    const hasAuditorRoleId = roleId === 'AUDITOR';
    // MANAGER, AUDITOR, ADMIN: same view as SUPERVISOR_NURSE but for every department (no LOF filter)
    const hasGlobalViewRole = rolesSet.some((r) =>
      GLOBAL_VIEW_ROLES.has(normalizeRoleId(r) ?? ''),
    );
    const isGlobalViewUser = hasAuditorRoleId || hasManagerRole || hasGlobalViewRole;

    const teamRoles = mergeTeamRoleInputs(user);
    const teamRoleTokens = teamRoles.map((r) => getTeamRoleToken(r)).filter(Boolean) as string[];
    const hasOCCTeamRole = teamRoleTokens.some((t) => t === 'OCC');
    const hasOCCParticipantRole = teamRoleTokens.some(
      (t) => normalizeTeamRoleId(t) === 'OCC_paricipant'
    );
    const fromFirestore = (user.teamCategoryIds ?? [])
      .map((id) => normalizeComplianceCategoryId(id))
      .filter((id): id is string => !!id);
    const fromMapping = Array.from(
      new Set(teamRoles.flatMap((role) => {
        const token = getTeamRoleToken(role);
        const key = typeof token === 'string' ? token.toUpperCase() : undefined;
        return key ? (TEAM_ROLE_CATEGORY_IDS[key] ?? []) : [];
      })),
    ).map((id) => normalizeComplianceCategoryId(id) ?? id).filter((id): id is string => !!id);
    // teamAllowedCategoryIds = team-based only (NN, ETHIC). Ethic team page requires ETHIC role.
    // HWN/SN see ethic on normal page via filterAllowedCategories (position-based).
    // Global roles (ADMIN/MANAGER/AUDITOR) get all team-surface categories so nav + /team/* match rules parity.
    const baseTeamAllowedCategoryIds = Array.from(
      new Set([...fromFirestore, ...fromMapping]),
    );
    const teamAllowedCategoryIds = isGlobalViewUser
      ? Array.from(new Set([...baseTeamAllowedCategoryIds, ...GLOBAL_VIEW_TEAM_CATEGORY_IDS]))
      : baseTeamAllowedCategoryIds;
    /** Normal-page category scope: not a duplicate of team mapping (useComplianceCategories merges both). */
    const allowedCategoryIds: string[] = [];

    const { departmentIds, lineOfWorkDepartmentIds } = computeDepartmentScope(
      generalRole,
      normalizedPositionRole,
      departmentRoles,
      user.departmentId,
      user.departmentIds,
    );
    const teamIds = computeTeamScope(teamRoles);

    // SN/SUPERVISOR_NURSE: lineOfWorkDepartmentIds are filled from Firestore LOF in AuthProvider (`mergeSupervisorLofScope`).
    // Resolver does not expand a static nursing line list.
    const userLineOfWorkKey = deriveUserLineOfWorkRoleKey(generalRole, normalizedPositionRole);
    const effectiveLineOfWorkDeptIds =
      isGlobalViewUser ? [] : lineOfWorkDepartmentIds;

    const roleContext: RoleContext = {
      generalRole,
      roles: rolesSet.length ? rolesSet : undefined,
      positionRole: normalizedPositionRole,
      departmentRole: pickPrimaryDepartmentRole(departmentRoles),
      departmentRoles: pickAllDepartmentRoles(departmentRoles),
      teamRole: pickPrimaryTeamRole(teamRoles) as TeamRole | undefined,
      teamRoles: teamRoleTokens.length ? teamRoleTokens : undefined,
      teamAllowedCategoryIds,
      primaryTeamId: pickPrimaryTeamId(teamRoles),
      primaryTeamCode: pickPrimaryTeamCode(teamRoles),
      teamIds,
      lineOfWorkDepartmentIds: effectiveLineOfWorkDeptIds,
      lofId: user.lofId,
      allowedCategoryIds,
      roleId,
      hasOCCTeamRole,
      hasOCCParticipantRole,
    };

    const effectiveDepartmentIds = isGlobalViewUser ? ['*'] : departmentIds;

    // Team page gate: restrict to users who hold the matching team_role (or a global role).
    // This centralizes the check that was previously done independently in each team dashboard component.
    if (page?.pageKind === 'team' && page.teamCategory) {
      const normalizedTeamCat = page.teamCategory.toUpperCase();
      const userTeamUpper = teamRoleTokens.map((t) => t.toUpperCase());
      const hasMatchingTeamRole = userTeamUpper.includes(normalizedTeamCat);

      if (!isGlobalViewUser && !hasMatchingTeamRole) {
        return {
          roleContext,
          scope: {
            view: { departmentIds: [], teamIds: [] },
            edit: { departmentIds: [], teamIds: [] },
            departmentIds: [],
            teamIds: [],
          },
        };
      }

      // Users with matching team role (or global role) see all departments on team pages
      return {
        roleContext,
        scope: {
          view: { departmentIds: ['*'], teamIds },
          edit: { departmentIds: isGlobalViewUser ? ['*'] : [], teamIds },
          departmentIds: ['*'],
          teamIds,
        },
      };
    }

    const scope: RoleScope = {
      view: {
        departmentIds: effectiveDepartmentIds,
        teamIds,
      },
      edit: {
        departmentIds: effectiveDepartmentIds,
        teamIds,
      },
      departmentIds: effectiveDepartmentIds,
      teamIds,
    };

    return { roleContext, scope };
  } catch (error) {
    console.error("Error resolving user role scope:", error);
    // Return a default, safe role scope
    return {
      roleContext: {
        generalRole: 'USER',
        positionRole: undefined,
        departmentRole: undefined,
        departmentRoles: [],
        teamRole: undefined,
        teamRoles: [],
        teamAllowedCategoryIds: [],
        primaryTeamId: undefined,
        primaryTeamCode: undefined,
        teamIds: [],
        lineOfWorkDepartmentIds: [],
        allowedCategoryIds: [],
      },
      scope: {
        view: {
          departmentIds: [],
          teamIds: [],
        },
        edit: {
          departmentIds: [],
          teamIds: [],
        },
        departmentIds: [],
        teamIds: [],
      },
    };
  }
};
